import { BridgeController } from '@/bridge/BridgeController'
import { type CommandBridge } from '@/bridge/CommandBridge'
import {
    createDisposer,
    createLogPrefix,
    type Disposable,
    type EventDispatcher,
    type Fun,
    IllegalArgumentError,
    IllegalStateError,
    isDisposable,
    logDebug,
    type LogTarget,
    onAny,
    type ReadonlyEventHost,
} from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

export type ObservableValueKeys<T> = {
    [P in keyof T]: T[P] extends ObservableValue<any> ? P : never
}[keyof T]

/**
 * The data type of the observable value.
 */
export type ObservableValueType<T> = T extends ObservableValue<infer V> ? V : never

export type FunctionKeys<T> = {
    [P in keyof T]: T[P] extends Fun ? P : never
}[keyof T]

export type NonFunctionKeys<T> = {
    [P in keyof T]: T[P] extends Fun ? never : P
}[keyof T]

/**
 * Types a function so the return type is always a promise
 */
export type WithPromiseReturn<F> = F extends (...args: infer A) => infer R
    ? (...args: A) => Promise<R extends Promise<any> ? Awaited<R> : R>
    : never

/**
 * Types a function so the return type is maybe a promise
 */
export type WithMaybePromiseReturn<F> = F extends (...args: infer A) => infer R
    ? (...args: A) => R | Awaited<R>
    : never

/**
 * Types a function so the return type is always void.
 */
export type WithVoidReturn<T> = T extends (...args: infer A) => any ? (...args: A) => void : never

export type BridgeDelegateOptions<T> = {
    readonly bridge: CommandBridge
    readonly localController?: T | null

    /**
     * If true, will transmit commands and receive events.
     * If false, localController must be defined.
     */
    readonly initiator: boolean

    /**
     * If true, will receive commands which delegate to local controller, and transmit events.
     * If true, localController must be defined.
     *
     * If both initiator and responder is true, the interface will be bidirectional.
     */
    readonly responder: boolean

    /**
     * If true (default), when the local controller is disposable, it will be disposed
     * when this bridge is disposed.
     */
    readonly disposeController?: boolean
}

export interface PropDelegate<T, K extends keyof T> {
    /**
     * Gets the last received property, or the local property if receive is false.
     */
    get(): T[K]

    /**
     * Sets the property and sends the new value over the bridge.
     * @param value
     */
    set(value: T[K]): void

    /**
     * Updates the property from the localController and sends it over the bridge.
     */
    update(): void
}

export type InferEventMap<T> = T extends ReadonlyEventHost<infer EventMap> ? EventMap : never

/**
 * Delegates commands, properties, and events over a {@link CommandBridge} to synchronize
 * a local controller with a remote counterpart. Acts as an initiator (sending commands),
 * a responder (handling incoming commands), or both for bidirectional communication.
 */
export class BridgeDelegate<T> implements LogTarget, Disposable {
    readonly logPrefix = createLogPrefix('BridgeDelegate')
    private readonly bridgeController: BridgeController
    private readonly disposer = createDisposer()

    constructor(private readonly options: BridgeDelegateOptions<T>) {
        const { add } = this.disposer
        if ((!options.initiator || options.responder) && !options.localController)
            throw new IllegalArgumentError('localController must be defined')
        this.bridgeController = add(
            new BridgeController({
                bridge: options.bridge,
            })
        )
    }

    createCommand<K extends FunctionKeys<T>>(prop: K): WithVoidReturn<T[K]> {
        return this._createCommand(prop, /* async */ false)
    }

    /**
     * Creates a command that expects a response.
     *
     * @param prop
     * @param timeout
     */
    createAsyncCommand<K extends FunctionKeys<T>>(
        prop: K,
        timeout?: number
    ): WithPromiseReturn<T[K]> {
        return this._createCommand(prop, /* async */ true, timeout)
    }

    private _createCommand(prop: keyof T, async: boolean, timeout?: number): any {
        const { add } = this.disposer
        const { localController, initiator, responder } = this.options
        const propStr = String(prop)
        const bridgeCmd = async
            ? this.bridgeController.createAsyncCommand(propStr, timeout)
            : this.bridgeController.createCommand(propStr)

        const localCmd = (...args: any[]) => {
            if (!localController)
                throw new IllegalStateError(
                    `received command '${propStr}, but no localController was set`
                )
            return (localController[prop] as Fun)(...args)
        }

        if (responder) {
            add(
                this.bridgeController.setHandler(propStr, (...args) => {
                    logDebug(this, `handled ${propStr} from bridge`, ...args)
                    return localCmd(...args)
                })
            )
        }

        return (...args: any[]): any => {
            if (initiator) return bridgeCmd(...args)
            else return localCmd(...args)
        }
    }

    createProperty<K extends NonFunctionKeys<T>>(prop: K, initialValue: T[K]): PropDelegate<T, K> {
        const { add } = this.disposer
        const { localController, responder, initiator } = this.options
        const propStr = String(prop)
        const updateKey = propStr + ':update'
        const updateCmd = this.bridgeController.createCommand(updateKey)
        const setKey = propStr + ':set'
        const setCmd = this.bridgeController.createCommand(updateKey)
        let receivedValue: T[K] = initialValue

        add(
            this.bridgeController.setHandler(updateKey, (v) => {
                receivedValue = v
            })
        )

        add(
            this.bridgeController.setHandler(setKey, (v) => {
                if (responder) localController![prop] = v
                else receivedValue = v
            })
        )

        return {
            get() {
                if (responder) return localController![prop]
                return receivedValue
            },

            set(v) {
                if (receivedValue === v) return
                receivedValue = v
                if (initiator) setCmd(receivedValue)
                else localController![prop] = receivedValue as any
            },

            update() {
                if (!localController)
                    throw new IllegalStateError('cannot call update without a localController')
                receivedValue = localController[prop]
                updateCmd(receivedValue)
            },
        }
    }

    /**
     * Creates a data provider which delegates over the bridge.
     */
    createObservableValue<K extends ObservableValueKeys<T>>(
        prop: K,
        initialValue: ObservableValueType<T[K]>
    ): T[K] {
        const { add } = this.disposer
        const propStr = String(prop)
        // Create a data controller, when an update is received over the bridge, update it.
        const { localController, initiator, responder } = this.options
        const dC = add(data(initialValue))
        let receiving = false
        if (initiator) {
            add(
                this.bridgeController.setHandler(String(prop), (value: any) => {
                    receiving = true
                    dC.value = value
                    receiving = false
                })
            )
        }

        // When the local value changes, update the delegate data controller.
        if (responder) {
            const localProvider = localController![prop] as ObservableValue<any>
            add(
                localProvider.onData((value) => {
                    dC.value = value
                })
            )
        }
        const bridgeCmd = this.bridgeController.createCommand(propStr)
        add(
            dC.onData((value) => {
                if (!receiving) {
                    logDebug(this, `sending data for ${propStr}`)
                    bridgeCmd(value)
                    logDebug(this, `done data for ${propStr}`)
                }
            })
        )
        return dC as T[K]
    }

    createEventHostDelegate(
        dispatcher: EventDispatcher<InferEventMap<T>>,
        keys: readonly (keyof InferEventMap<T>)[]
    ) {
        const { add } = this.disposer
        const { localController, initiator, responder } = this.options

        const bridgeCmd = this.bridgeController.createCommand('on')
        if (responder) {
            add(
                onAny(localController as any, keys as any, (event, type) => {
                    bridgeCmd(event, type)
                    dispatcher.dispatch(type, event as any)
                })
            )
        }
        if (initiator) {
            add(
                this.bridgeController.setHandler('on', (event, type) => {
                    dispatcher.dispatch(type, event)
                })
            )
        }
    }

    dispose(): void {
        logDebug(this, 'dispose')
        this.disposer.dispose()
        const localController = this.options.localController
        if ((this.options.disposeController ?? true) && isDisposable(localController)) {
            localController.dispose()
        }
    }
}
